import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users,
  UserPlus,
  Shield,
  BookOpen,
  Eye,
  Search,
  RefreshCcw,
  ExternalLink,
  Briefcase,
  FileText,
  MessageSquareText,
  MessageSquare,
  X as XIcon,
  Mail,
  MapPin,
  Calendar,
  FlaskConical,
  Lock,
} from "lucide-react";
import { SiteShell } from "@/components/layout/SiteShell";
import { AdminMessageDialog } from "@/components/marketplace/AdminMessageDialog";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";

const ROLE_OPTIONS = [
  { value: "", label: "All roles" },
  { value: "author", label: "Authors" },
  { value: "beta_reader", label: "Beta readers" },
  { value: "admin", label: "Admins" },
];

function roleChipClass(role) {
  // Solid, high-contrast pill palette so every role reads clearly on the
  // warm honey row background at a glance — no hover required. Each
  // pill uses a solid brand-token background paired with a
  // brightness-tuned cream/ink text so contrast holds in both light and
  // dark mode without altering existing design tokens.
  if (role === "admin")       return "bg-[hsl(var(--sb-forest))] text-[hsl(40_60%_96%)] border-[hsl(var(--sb-forest))] font-semibold";
  if (role === "author")      return "bg-[hsl(var(--sb-clay))] text-[hsl(40_60%_96%)] border-[hsl(var(--sb-clay))] font-semibold";
  if (role === "beta_reader") return "bg-[hsl(var(--sb-sage))] text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-sage))] font-semibold";
  return "bg-[hsl(var(--sb-gold))] text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-gold))] font-semibold";
}

export default function AdminUsers() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");
  const [q, setQ] = useState("");
  const [includeDemo, setIncludeDemo] = useState(false);
  const [verifiedFilter, setVerifiedFilter] = useState(""); // "" | "yes" | "no"
  const [selected, setSelected] = useState(null);

  const isAdmin = (user?.roles || []).includes("admin");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [listRes, statsRes] = await Promise.all([
        api.get("/users/admin/list", { params: { role: role || undefined, q: q || undefined, include_demo: includeDemo, verified: verifiedFilter || undefined, limit: 200 } }),
        api.get("/users/admin/stats"),
      ]);
      setItems(listRes.data.items);
      setTotal(listRes.data.total);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [role, q, includeDemo, verifiedFilter]);

  useEffect(() => {
    if (isAdmin) load();
  }, [isAdmin, load]);

  if (user === undefined) return null;
  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  return (
    <SiteShell>
      <section className="container-page py-10" data-testid="admin-users-root">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-serif text-4xl tracking-tight">Registered users</h1>
            <p className="mt-2 text-muted-foreground max-w-xl">
              Non-sensitive account overview — email, profile summary, activity counts. Passwords, tokens, and payment data are never included.
            </p>
          </div>
          <button
            type="button"
            onClick={load}
            data-testid="admin-users-refresh"
            className="btn-ghost inline-flex items-center gap-2"
          >
            <RefreshCcw size={16} /> Refresh
          </button>
        </div>

        {/* Stat cards */}
        {stats && (
          <div className="mt-8 grid grid-cols-2 md:grid-cols-6 gap-3">
            <StatCard label="Real users" value={stats.real_users} Icon={Users} tint="forest" testid="stat-real-users" />
            <StatCard label="Signups · 7d" value={stats.signups_last_7_days} Icon={UserPlus} tint="gold" testid="stat-signups-7d" />
            <StatCard label="Authors" value={stats.authors} Icon={BookOpen} tint="gold" />
            <StatCard label="Beta readers" value={stats.beta_readers} Icon={Eye} tint="sage" />
            <StatCard label="Admins" value={stats.admins} Icon={Shield} tint="forest" />
            <StatCard label="Demo accounts" value={stats.demo_accounts} Icon={FlaskConical} tint="muted" testid="stat-demo" />
          </div>
        )}

        {/* Filters */}
        <div className="mt-8 flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search email or display name…"
              data-testid="admin-users-search"
              className="sb-input pl-9 w-72"
            />
          </div>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            data-testid="admin-users-role-filter"
            className="sb-input w-auto"
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <select
            value={verifiedFilter}
            onChange={(e) => setVerifiedFilter(e.target.value)}
            data-testid="admin-users-verified-filter"
            className="sb-input w-auto"
            title="Filter by email verification status"
          >
            <option value="">Any verification</option>
            <option value="yes">Verified</option>
            <option value="no">Unverified</option>
          </select>
          <label className="inline-flex items-center gap-2 text-sm select-none cursor-pointer rounded-lg border border-border bg-card px-3 py-2 hover:border-[hsl(var(--sb-forest)/0.4)]">
            <input
              type="checkbox"
              checked={includeDemo}
              onChange={(e) => setIncludeDemo(e.target.checked)}
              data-testid="admin-users-show-demo"
              className="rounded"
            />
            <FlaskConical size={14} className="text-muted-foreground" />
            Show demo accounts
          </label>
          <div className="ml-auto text-sm text-muted-foreground">
            {loading ? "Loading…" : `${items.length} of ${total} shown`}
          </div>
        </div>

        {/* Table */}
        <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-[hsl(var(--sb-forest)/0.03)] text-left">
              <tr>
                <Th>User</Th>
                <Th>Roles</Th>
                <Th>Profile</Th>
                <Th className="text-right">Activity</Th>
                <Th>Joined</Th>
                <Th></Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">Loading users…</td></tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-muted-foreground" data-testid="admin-users-empty">
                    No users match those filters.
                  </td>
                </tr>
              ) : items.map((u) => (
                <tr key={u.id} className="border-t border-border/60 hover:bg-[hsl(var(--sb-forest)/0.02)]" data-testid={`admin-users-row-${u.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => setSelected(u)}
                        className="shrink-0 rounded-full focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
                        aria-label={`Open details for ${u.display_name || u.email}`}
                        data-testid={`admin-users-avatar-open-${u.id}`}
                      >
                        <Avatar url={u.avatar_url} name={u.display_name || u.email} />
                      </button>
                      <div className="min-w-0">
                        <div className="font-medium truncate flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => setSelected(u)}
                            className="truncate text-left hover:underline underline-offset-2 min-h-[32px]"
                            data-testid={`admin-users-name-open-${u.id}`}
                          >
                            {u.display_name || <span className="italic text-muted-foreground">(no name)</span>}
                          </button>
                          {u.is_demo && (
                            <span
                              className="chip border text-[10px] !bg-[hsl(var(--sb-moss))] !text-[hsl(40_60%_96%)] !border-[hsl(var(--sb-moss))] font-semibold inline-flex items-center gap-1"
                              data-testid={`admin-users-demo-badge-${u.id}`}
                              title="Demo/seeded account — hidden from real users"
                            >
                              <FlaskConical size={10} /> demo
                            </span>
                          )}
                          {u.is_private_test && (
                            <span
                              className="chip text-[10px] border !bg-[hsl(var(--sb-clay))] !text-[hsl(40_60%_96%)] !border-[hsl(var(--sb-clay))] font-semibold inline-flex items-center gap-1"
                              data-testid={`admin-users-pta-badge-${u.id}`}
                              title="Private Test Group — peer-visible to other PTAs, hidden from real users, no demo content."
                            >
                              <Lock size={10} /> private test
                            </span>
                          )}
                          {u.email_verified ? (
                            <span
                              className="chip text-[10px] border !bg-[hsl(var(--sb-forest))] !text-[hsl(40_60%_96%)] !border-[hsl(var(--sb-forest))] font-semibold inline-flex items-center gap-1"
                              data-testid={`admin-users-verified-badge-${u.id}`}
                              title={
                                u.email_verified_at
                                  ? `Verified ${new Date(u.email_verified_at).toLocaleDateString()} (${u.email_verification_source || "email"})`
                                  : "Verified"
                              }
                            >
                              verified
                            </span>
                          ) : (
                            <span
                              className="chip text-[10px] border !bg-amber-500 !text-amber-950 !border-amber-500 font-semibold inline-flex items-center gap-1"
                              data-testid={`admin-users-unverified-badge-${u.id}`}
                              title="Email not yet verified — sensitive member-to-member actions are gated for this user."
                            >
                              unverified
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap gap-1">
                      {(u.roles || []).map((r) => (
                        <span key={r} className={`chip border text-[10px] ${roleChipClass(r)}`}>{r}</span>
                      ))}
                    </div>
                  </td>
                  <td className="p-3">
                    <ProfileBar pct={u.profile_completion} />
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-3 text-xs text-muted-foreground">
                      <span title="Projects created" className="inline-flex items-center gap-1"><Briefcase size={12} /> {u.jobs_created}</span>
                      <span title="Applications submitted" className="inline-flex items-center gap-1"><FileText size={12} /> {u.applications_submitted}</span>
                      <span title="Feedback submitted" className="inline-flex items-center gap-1"><MessageSquareText size={12} /> {u.feedback_submitted}</span>
                    </div>
                  </td>
                  <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">
                    {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button
                      type="button"
                      onClick={() => setSelected(u)}
                      data-testid={`admin-users-view-${u.id}`}
                      className="text-[hsl(var(--sb-forest-2))] text-xs hover:underline"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {selected && (
          <UserDetail
            user={selected}
            viewerId={user?.id}
            onClose={() => setSelected(null)}
            onPrivateTestChanged={(next) => {
              setSelected((prev) => (prev ? { ...prev, is_private_test: next } : prev));
              setItems((us) => us.map((u) => (u.id === selected.id ? { ...u, is_private_test: next } : u)));
            }}
            onEmailVerifiedChanged={(next) => {
              setSelected((prev) => (prev ? { ...prev, email_verified: next } : prev));
              setItems((us) => us.map((u) => (u.id === selected.id ? { ...u, email_verified: next } : u)));
            }}
          />
        )}
      </section>
    </SiteShell>
  );
}

function Th({ children, className = "" }) {
  return <th className={`p-3 text-xs uppercase tracking-wide text-muted-foreground font-medium ${className}`}>{children}</th>;
}

function StatCard({ label, value, Icon, tint, testid }) {
  const cls = {
    forest: "bg-[hsl(var(--sb-forest)/0.08)] text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-forest)/0.25)]",
    gold: "bg-[hsl(var(--sb-gold)/0.12)] text-[hsl(var(--sb-gold-2))] border-[hsl(var(--sb-gold)/0.35)]",
    sage: "bg-[hsl(var(--sb-sage)/0.15)] text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-sage)/0.4)]",
    muted: "bg-muted/40 text-muted-foreground border-border",
  }[tint] || "bg-muted text-muted-foreground border-border";
  return (
    <div className={`rounded-2xl border p-4 ${cls}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wide opacity-80">{label}</span>
        <Icon size={16} />
      </div>
      <div className="mt-1 font-serif text-3xl leading-none">{value}</div>
    </div>
  );
}

function ProfileBar({ pct }) {
  const color = pct >= 80 ? "bg-[hsl(var(--sb-forest))]" : pct >= 40 ? "bg-[hsl(var(--sb-gold))]" : "bg-[hsl(var(--sb-clay))]";
  return (
    <div className="w-32">
      <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 text-[11px] text-muted-foreground">{pct}% complete</div>
    </div>
  );
}

function Avatar({ url, name }) {
  const initial = (name || "?").charAt(0).toUpperCase();
  if (url) {
    return (
      <img
        src={url}
        alt=""
        className="w-9 h-9 rounded-full object-cover bg-muted shrink-0"
      />
    );
  }
  return (
    <div className="w-9 h-9 rounded-full bg-[hsl(var(--sb-moss))] text-[hsl(var(--sb-forest-2))] grid place-items-center font-medium shrink-0">
      {initial}
    </div>
  );
}

function UserDetail({ user, viewerId, onClose, onPrivateTestChanged, onEmailVerifiedChanged }) {
  const [showMessage, setShowMessage] = useState(false);
  const canMessage = user.id !== viewerId && !user.is_demo;
  const [drawer, setDrawer] = useState(null); // 'applications' | 'projects' | 'feedback'
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  const open = async (kind) => {
    setDrawer(kind);
    if (data[kind] !== undefined) return;
    setLoading(true);
    try {
      const { data: res } = await api.get(`/users/admin/${user.id}/${kind}`);
      setData((prev) => ({ ...prev, [kind]: res.items || [] }));
    } catch (err) {
      toast.error(formatApiError(err));
      setData((prev) => ({ ...prev, [kind]: [] }));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      data-testid="admin-users-detail"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative w-full sm:max-w-2xl bg-card border border-border rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="flex items-start justify-between p-5 border-b border-border/60 bg-[hsl(var(--sb-forest)/0.03)]">
          <div className="flex items-center gap-3 min-w-0">
            <Avatar url={user.avatar_url} name={user.display_name || user.email} />
            <div className="min-w-0">
              <h2 className="font-serif text-xl truncate">{user.display_name || "(no display name)"}</h2>
              <p className="text-xs text-muted-foreground inline-flex items-center gap-1 truncate">
                <Mail size={11} /> {user.email}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="admin-users-detail-close"
            aria-label="Close"
            className="rounded-full p-2 hover:bg-muted text-muted-foreground shrink-0"
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="overflow-y-auto p-5 space-y-5">
          <div className="flex flex-wrap gap-1">
            {user.is_demo && (
              <span className="chip border text-xs !bg-[hsl(var(--sb-moss))] !text-[hsl(40_60%_96%)] !border-[hsl(var(--sb-moss))] font-semibold inline-flex items-center gap-1" data-testid="admin-users-detail-demo-badge">
                <FlaskConical size={11} /> demo account
              </span>
            )}
            {user.is_private_test && (
              <span className="chip text-xs border !bg-[hsl(var(--sb-clay))] !text-[hsl(40_60%_96%)] !border-[hsl(var(--sb-clay))] font-semibold inline-flex items-center gap-1" data-testid="admin-users-detail-pta-badge">
                <Lock size={11} /> private test
              </span>
            )}
            {(user.roles || []).map((r) => (
              <span key={r} className={`chip border text-xs ${roleChipClass(r)}`}>{r}</span>
            ))}
          </div>

          {!(user.roles || []).includes("admin") && (
            <PrivateTestToggle user={user} onChange={onPrivateTestChanged} />
          )}

          <EmailVerifiedToggle user={user} onChange={onEmailVerifiedChanged} />

          {user.headline && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Headline</div>
              <p className="mt-1 text-sm">{user.headline}</p>
            </div>
          )}

          {user.bio && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Bio</div>
              <p className="mt-1 text-sm whitespace-pre-wrap">{user.bio}</p>
            </div>
          )}

          {user.genres && user.genres.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Genres</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {user.genres.map((g) => (
                  <span key={g} className="chip border border-[hsl(var(--sb-gold)/0.4)] bg-[hsl(var(--sb-gold)/0.1)] text-[hsl(var(--sb-gold-2))] text-xs">{g}</span>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border/60">
            {user.location && (
              <div>
                <div className="text-xs uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1"><MapPin size={11} /> Location</div>
                <p className="mt-1 text-sm">{user.location}</p>
              </div>
            )}
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground inline-flex items-center gap-1"><Calendar size={11} /> Signed up</div>
              <p className="mt-1 text-sm">{user.created_at ? new Date(user.created_at).toLocaleString() : "—"}</p>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Profile completion</div>
              <div className="mt-1"><ProfileBar pct={user.profile_completion} /></div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 pt-2 border-t border-border/60">
            <ClickableActivityCard
              Icon={Briefcase}
              label="Projects posted"
              value={user.jobs_created}
              onClick={() => open("projects")}
              testId="admin-users-open-projects"
            />
            <ClickableActivityCard
              Icon={FileText}
              label="Applications"
              value={user.applications_submitted}
              onClick={() => open("applications")}
              testId="admin-users-open-applications"
            />
            <ClickableActivityCard
              Icon={MessageSquareText}
              label="Feedback"
              value={user.feedback_submitted}
              onClick={() => open("feedback")}
              testId="admin-users-open-feedback"
            />
          </div>

          <div className="pt-3 border-t border-border/60 flex flex-wrap items-center gap-3">
            {canMessage && (
              <button
                type="button"
                onClick={() => setShowMessage(true)}
                className="btn-primary inline-flex items-center gap-2 min-h-[44px]"
                data-testid="admin-users-detail-message"
              >
                <MessageSquare size={15} /> Message
              </button>
            )}
            <Link
              to={`/profile/${user.id}`}
              onClick={onClose}
              className="text-sm text-[hsl(var(--sb-forest-2))] inline-flex items-center gap-1 hover:underline min-h-[44px]"
              data-testid="admin-users-view-public-profile"
            >
              View public profile <ExternalLink size={12} />
            </Link>
          </div>
          {canMessage && (
            <AdminMessageDialog
              open={showMessage}
              onClose={() => setShowMessage(false)}
              otherUserId={user.id}
              displayName={user.display_name || user.email}
            />
          )}
        </div>

        {drawer && (
          <ActivityDrawer
            user={user}
            kind={drawer}
            items={data[drawer] ?? null}
            loading={loading}
            onClose={() => setDrawer(null)}
          />
        )}
      </div>
    </div>
  );
}

function ActivityCard({ Icon, label, value }) {
  return (
    <div className="rounded-xl border border-border p-3">
      <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
        <span>{label}</span>
        <Icon size={12} />
      </div>
      <div className="mt-1 font-serif text-2xl leading-none">{value}</div>
    </div>
  );
}

function ClickableActivityCard({ Icon, label, value, onClick, testId }) {
  const disabled = !value;
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className="text-left group"
      data-testid={testId}
      aria-label={`View this user's ${label.toLowerCase()}`}
    >
      <div className={`rounded-xl border border-border p-3 transition-all ${
        disabled
          ? "opacity-70 cursor-not-allowed"
          : "hover:border-[hsl(var(--sb-forest)/0.5)] hover:shadow-sm cursor-pointer"
      }`}>
        <div className="flex items-center justify-between text-xs uppercase tracking-wide text-muted-foreground">
          <span>{label}</span>
          <Icon size={12} />
        </div>
        <div className={`mt-1 font-serif text-2xl leading-none ${disabled ? "" : "text-[hsl(var(--sb-forest-2))]"}`}>
          {value}
        </div>
        <div className="mt-1 text-[10px] text-muted-foreground">
          {disabled ? "None yet" : "Click to view →"}
        </div>
      </div>
    </button>
  );
}


const APP_STATUS_CHIP = {
  pending: "bg-[hsl(var(--sb-gold)/0.15)] text-[hsl(var(--sb-gold-2))] border-[hsl(var(--sb-gold)/0.4)]",
  accepted: "bg-[hsl(var(--sb-forest)/0.12)] text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-forest)/0.3)]",
  declined: "bg-[hsl(var(--sb-clay)/0.15)] text-[hsl(var(--sb-clay-2))] border-[hsl(var(--sb-clay)/0.4)]",
  withdrawn: "bg-muted text-muted-foreground border-border",
};

const PROJECT_STATUS_CHIP = {
  open: "bg-[hsl(var(--sb-forest)/0.1)] text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-forest)/0.3)]",
  in_progress: "bg-[hsl(var(--sb-gold)/0.15)] text-[hsl(var(--sb-gold-2))] border-[hsl(var(--sb-gold)/0.4)]",
  completed: "bg-[hsl(var(--sb-sage)/0.15)] text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-sage)/0.4)]",
  closed: "bg-muted text-muted-foreground border-border",
  cancelled: "bg-[hsl(var(--sb-clay)/0.15)] text-[hsl(var(--sb-clay-2))] border-[hsl(var(--sb-clay)/0.4)]",
};

const FEEDBACK_STATUS_CHIP = {
  new: "bg-[hsl(var(--sb-clay)/0.15)] text-[hsl(var(--sb-clay-2))] border-[hsl(var(--sb-clay)/0.4)]",
  in_review: "bg-[hsl(var(--sb-gold)/0.15)] text-[hsl(var(--sb-gold-2))] border-[hsl(var(--sb-gold)/0.4)]",
  planned: "bg-[hsl(var(--sb-sage)/0.15)] text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-sage)/0.4)]",
  resolved: "bg-[hsl(var(--sb-forest)/0.12)] text-[hsl(var(--sb-forest-2))] border-[hsl(var(--sb-forest)/0.3)]",
  wont_fix: "bg-muted text-muted-foreground border-border",
};

const DRAWER_META = {
  applications: { title: "Applications", empty: "This user hasn’t applied to any projects.", Icon: FileText },
  projects: { title: "Projects posted", empty: "This user hasn’t posted any projects.", Icon: Briefcase },
  feedback: { title: "Feedback submissions", empty: "This user hasn’t submitted any feedback yet.", Icon: MessageSquareText },
};

function ActivityDrawer({ user, kind, items, loading, onClose }) {
  const meta = DRAWER_META[kind];
  return (
    <div
      className="absolute inset-0 z-10 bg-card flex flex-col"
      role="dialog"
      aria-modal="true"
      data-testid={`admin-user-${kind}-drawer`}
    >
      <div className="flex items-start justify-between p-5 border-b border-border/60 bg-[hsl(var(--sb-forest)/0.03)]">
        <div className="min-w-0">
          <h2 className="font-serif text-xl">{meta.title}</h2>
          <p className="text-xs text-muted-foreground truncate">
            {user.display_name || user.email} · {user.email}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          data-testid={`admin-user-${kind}-close`}
          aria-label={`Close ${meta.title.toLowerCase()} view`}
          className="rounded-full p-2 hover:bg-muted text-muted-foreground shrink-0"
        >
          <XIcon size={18} />
        </button>
      </div>

      <div className="overflow-y-auto p-5 space-y-3 flex-1">
        {loading || items === null ? (
          <div className="text-center py-10 text-muted-foreground">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed rounded-2xl" data-testid={`admin-user-${kind}-empty`}>
            <meta.Icon size={40} className="mx-auto text-muted-foreground" />
            <p className="mt-3 font-medium">Nothing here yet</p>
            <p className="text-sm text-muted-foreground">{meta.empty}</p>
          </div>
        ) : (
          items.map((row) => {
            if (kind === "applications") return <ApplicationRow key={row.id} row={row} onNavigate={onClose} />;
            if (kind === "projects") return <ProjectRow key={row.id} row={row} onNavigate={onClose} />;
            return <FeedbackRow key={row.id} row={row} />;
          })
        )}
      </div>
    </div>
  );
}

function ApplicationRow({ row, onNavigate }) {
  const chip = APP_STATUS_CHIP[row.status] || APP_STATUS_CHIP.pending;
  return (
    <div className="rounded-xl border border-border p-4" data-testid={`admin-user-application-${row.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`chip border text-[10px] ${chip}`}>{row.status}</span>
        {row.job_status && (
          <span className="chip border text-[10px] bg-muted/50 border-border text-muted-foreground">
            project: {row.job_status.replace(/_/g, " ")}
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          {row.created_at ? new Date(row.created_at).toLocaleString() : "\u2014"}
        </span>
      </div>
      {row.job_id ? (
        <Link
          to={`/jobs/${row.job_id}`}
          onClick={onNavigate}
          className="mt-2 block font-medium text-[hsl(var(--sb-forest-2))] hover:underline truncate"
        >
          {row.job_title}
        </Link>
      ) : (
        <p className="mt-2 font-medium italic text-muted-foreground">{row.job_title}</p>
      )}
      {row.author_display_name && (
        <p className="text-xs text-muted-foreground mt-0.5">
          by {row.author_display_name}
          {row.author_email && <span> · {row.author_email}</span>}
        </p>
      )}
      {row.proposed_rate != null && (
        <p className="text-xs text-muted-foreground mt-0.5">Proposed rate: ${row.proposed_rate}</p>
      )}
      {row.cover_letter && (
        <details className="mt-3">
          <summary className="text-xs text-muted-foreground cursor-pointer hover:text-foreground">Cover letter</summary>
          <p className="mt-2 text-sm whitespace-pre-wrap text-muted-foreground">{row.cover_letter}</p>
        </details>
      )}
    </div>
  );
}

function ProjectRow({ row, onNavigate }) {
  const chip = PROJECT_STATUS_CHIP[row.status] || PROJECT_STATUS_CHIP.open;
  const budgetLabel = row.budget_min != null || row.budget_max != null
    ? `$${row.budget_min ?? "?"}\u2013$${row.budget_max ?? "?"}`
    : null;
  return (
    <div className="rounded-xl border border-border p-4" data-testid={`admin-user-project-${row.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`chip border text-[10px] ${chip}`}>{(row.status || "open").replace(/_/g, " ")}</span>
        {row.escrow_status && (
          <span className="chip border text-[10px] bg-muted/50 border-border text-muted-foreground">
            escrow: {row.escrow_status}
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          {row.created_at ? new Date(row.created_at).toLocaleString() : "\u2014"}
        </span>
      </div>
      <Link
        to={`/jobs/${row.id}`}
        onClick={onNavigate}
        className="mt-2 block font-medium text-[hsl(var(--sb-forest-2))] hover:underline truncate"
      >
        {row.title || "(untitled project)"}
      </Link>
      <div className="mt-1 text-xs text-muted-foreground flex flex-wrap gap-x-3 gap-y-1">
        {row.genre && <span>Genre: {row.genre}</span>}
        {row.word_count != null && <span>{row.word_count.toLocaleString()} words</span>}
        {budgetLabel && <span>Budget: {budgetLabel}</span>}
        {row.turnaround_days != null && <span>{row.turnaround_days}d turnaround</span>}
        <span>{row.applications_count} application{row.applications_count === 1 ? "" : "s"}</span>
      </div>
    </div>
  );
}

function FeedbackRow({ row }) {
  const chip = FEEDBACK_STATUS_CHIP[row.status] || FEEDBACK_STATUS_CHIP.new;
  return (
    <div className="rounded-xl border border-border p-4" data-testid={`admin-user-feedback-${row.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="chip border text-[10px] bg-muted/50 border-border text-muted-foreground">
          {row.type}
        </span>
        <span className={`chip border text-[10px] ${chip}`}>{(row.status || "new").replace(/_/g, " ")}</span>
        <span className="text-xs text-muted-foreground">
          {row.created_at ? new Date(row.created_at).toLocaleString() : "\u2014"}
        </span>
      </div>
      <p className="mt-2 text-sm whitespace-pre-wrap">{row.description}</p>
      {row.page_url && (
        <p className="mt-1 text-xs text-muted-foreground truncate">
          Page: <code className="font-mono">{row.page_url}</code>
        </p>
      )}
      {row.admin_note && (
        <div className="mt-2 rounded-lg bg-[hsl(var(--sb-forest)/0.05)] border border-[hsl(var(--sb-forest)/0.2)] px-3 py-2">
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Admin note</div>
          <p className="mt-0.5 text-xs">{row.admin_note}</p>
        </div>
      )}
    </div>
  );
}


function PrivateTestToggle({ user, onChange }) {
  const [busy, setBusy] = useState(false);
  const enabled = !!user.is_private_test;
  const toggle = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/users/admin/${user.id}/private-test`, {
        is_private_test: !enabled,
      });
      onChange?.(data.is_private_test);
      toast.success(
        data.is_private_test
          ? "Added to the private test group — peer-visible to other PTAs, hidden from the public."
          : "Cleared Private Test flag — visibility follows normal beta rules."
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div
      className="rounded-xl border border-dashed border-[hsl(var(--sb-clay))]/40 bg-[hsl(var(--sb-clay))]/5 p-4"
      data-testid="admin-users-pta-toggle-card"
    >
      <div className="flex items-start gap-3">
        <div className="rounded-lg bg-[hsl(var(--sb-clay))]/15 p-2 text-[hsl(var(--sb-clay))]">
          <Lock size={16} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-[hsl(var(--sb-clay))]">Private Test Group</div>
          <p className="mt-0.5 text-xs text-muted-foreground">
            {enabled
              ? "In the private test group — sees and interacts with other PTAs + admins. Hidden from the public. Demo content is filtered out."
              : "Flip on to add this user to the private test group. Group members see each other's profiles, projects, applications, and messages — but stay invisible to real users and see no demo content."}
          </p>
          <button
            type="button"
            onClick={toggle}
            disabled={busy}
            data-testid="admin-users-pta-toggle-btn"
            className={`mt-3 ${enabled ? "btn-ghost border" : "btn-primary"} text-xs`}
          >
            {busy ? "Saving…" : enabled ? "Remove from test group" : "Add to test group"}
          </button>
        </div>
      </div>
    </div>
  );
}


function EmailVerifiedToggle({ user, onChange }) {
  const [busy, setBusy] = useState(false);
  const verified = !!user.email_verified;

  const markVerified = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/users/admin/${user.id}/mark-verified`);
      onChange?.(!!data.email_verified);
      toast.success(data.already_verified ? "User was already verified." : "User marked as verified.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-2xl border border-border bg-card p-4 flex items-start gap-3"
      data-testid="admin-users-email-verified-card"
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Mail size={14} className="text-muted-foreground" />
          <div className="text-sm font-medium">Email verification</div>
          <span
            className={`chip text-[10px] border ${
              verified
                ? "border-[hsl(var(--sb-forest))]/40 bg-[hsl(var(--sb-forest))]/10 text-[hsl(var(--sb-forest-2))]"
                : "border-amber-500/40 bg-amber-500/10 text-amber-700"
            }`}
          >
            {verified ? "verified" : "unverified"}
          </span>
        </div>
        {verified && user.email_verified_at && (
          <p className="mt-1 text-xs text-muted-foreground">
            Verified {new Date(user.email_verified_at).toLocaleString()} · {user.email_verification_source || "email"}
          </p>
        )}
        {!verified && (
          <p className="mt-1 text-xs text-muted-foreground">
            This user cannot send messages, apply, or start Coffee Chats until they verify. Mark manually as an override — every action is audit-logged.
          </p>
        )}
      </div>
      {!verified && (
        <button
          type="button"
          onClick={markVerified}
          disabled={busy}
          className="btn-secondary text-xs shrink-0"
          data-testid="admin-users-mark-verified-btn"
        >
          {busy ? "Marking…" : "Mark verified"}
        </button>
      )}
    </div>
  );
}
